import { AfterViewInit, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  FilterCondition,
  FilterDTO,
  FilterOperator,
  ParkerTagClient,
  SortDirection,
} from '../../../base/models/models.swagger.generated';
import { IAutocompleteItem } from '../../../base/shared/autocomplete/autocomplete-item';
import { AutocompleteComponent } from '../../../base/shared/autocomplete/autocomplete.component';
import { searchScore } from '../../../base/shared/autocomplete/search-score';
import { TranslateService } from '@ngx-translate/core';
import { LICENSE_PLATE_MIN_LENGTH } from '../../../base/shared/filter-util';

@Component({
  selector: 'app-license-plate-autocomplete',
  template: `
    <app-autocomplete
      [getDataFunction]="searchPlates.bind(this)"
      [minLength]="1"
      (valueChange)="valueChange.emit($event ?? null)"
      [placeholder]="placeholder"
      [autoSelectOnSingleResult]="true">
    </app-autocomplete>
  `,
  imports: [AutocompleteComponent],
})
export class LicensePlateAutocompleteComponent implements AfterViewInit {
  @Input() clientId?: number;

  @Input() set value(val: string | null) {
    if (!val) {
      this.autocompleteRef?.clear();
    }
  }

  @Input() initialText?: string;
  @Input() placeholder = 'i18n.domain.parker-registration.filter.license-plate';
  @Output() valueChange = new EventEmitter<string | null>();

  @ViewChild(AutocompleteComponent) private autocompleteRef: AutocompleteComponent;

  constructor(private parkerTagClient: ParkerTagClient, private translate: TranslateService) {}

  ngAfterViewInit(): void {
    const ac = this.autocompleteRef.autocomplete;

    if (this.initialText) {
      ac.text = this.initialText;
    }

    ac.element.addEventListener('input', (e: Event) => {
      const text = (e.target as HTMLInputElement).value.trim();
      const parts = text ? text.split(/[,;]/).map(p => p.trim()) : [];
      const lastPlate = parts[parts.length - 1] ?? '';
      ac.noRecordsTemplate = lastPlate.length > 0 && lastPlate.length < LICENSE_PLATE_MIN_LENGTH
        ? this.translate.instant('i18n.domain.parker-registration.filter.license-plate-min-length')
        : 'No records found';

      if (!text) {
        this.valueChange.emit(null);
        return;
      }
      const validPlates = parts.filter(p => p.length >= 2);
      this.valueChange.emit(validPlates.length > 0 ? text : null);
    });
  }

  public searchPlates(searchText: string): Observable<IAutocompleteItem[]> {
    if (!searchText?.trim()) {
      return of([]);
    }

    const parts = searchText.split(/[,;]/).map(p => p.trim());
    const lastPlate = parts[parts.length - 1] ?? '';
    const prefix = parts.slice(0, -1).join(', ');

    if (!lastPlate || lastPlate.length < LICENSE_PLATE_MIN_LENGTH) {
      return of([]);
    }

    const plateFilter = new FilterDTO({
      isComplex: true,
      condition: FilterCondition.Or,
      predicates: [
        new FilterDTO({ isComplex: false, field: 'OperationalControlPlate', operator: FilterOperator.Contains, value: lastPlate }),
        new FilterDTO({ isComplex: false, field: 'ControlPlateNumber', operator: FilterOperator.Contains, value: lastPlate }),
      ],
    });

    return this.parkerTagClient.searchTags(this.clientId, [plateFilter], 0, 20, 'OperationalControlPlate', SortDirection.Ascending)
      .pipe(map(result => {
        const seen = new Set<string>();
        const searchLower = lastPlate.toLowerCase();

        return result.items
          .map(tag => tag.operationalControlPlate ?? tag.normalizedControlPlate ?? '')
          .filter(plate => plate && !seen.has(plate) && !!seen.add(plate))
          .sort((a, b) => searchScore(b, searchLower) - searchScore(a, searchLower) || a.localeCompare(b))
          .map(plate => {
            const combined = prefix ? `${prefix}, ${plate}` : plate;
            return { id: combined, name: combined };
          });
      }));
  }
}
